import {
  Component,
  input, // 1. استيراد input من angular/core
  OnDestroy,
  signal,
  inject,
  PLATFORM_ID,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import type * as Leaflet from 'leaflet';
import { LocationIcon } from '../../../../shared/ui/icons/location-icon/location-icon';
import { SearchIcon } from '../../../../shared/ui/icons/search-icon/search-icon';
import { CurrentLocationIcon } from '../../../../shared/ui/icons/current-location-icon/current-location-icon';
import { ClosePreviewIcon } from '../../../../shared/ui/icons/close-preview-icon/close-preview-icon';
import { WarningMessage } from '../../../../shared/ui/messages/warning-message/warning-message';

@Component({
  selector: 'app-pharmacy-location',
  standalone: true,
  imports: [ReactiveFormsModule, LocationIcon, SearchIcon, CurrentLocationIcon, ClosePreviewIcon, WarningMessage],
  templateUrl: './pharmacy-location.html',
  styleUrl: './pharmacy-location.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PharmacyLocation implements OnDestroy {
  pharmacyForm = input.required<FormGroup>();

  @ViewChild('mapElement') mapElementRef?: ElementRef<HTMLDivElement>;

  private platformId = inject(PLATFORM_ID);

  isMapOpen = signal(false);
  isLocating = signal(false);
  locationError = signal<string | null>(null);
  selectedLat = signal<number | null>(null);
  selectedLng = signal<number | null>(null);

  private map: Leaflet.Map | null = null;
  private marker: Leaflet.Marker | null = null;
  private L: typeof Leaflet | null = null;

  private readonly defaultLat = 30.0444;
  private readonly defaultLng = 31.2357;

  openMap(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.parseExistingLocation();
    this.locationError.set(null);
    this.isMapOpen.set(true);

    setTimeout(() => this.initMap(), 150);
  }

  closeMap(): void {
    this.destroyMap();
    this.isMapOpen.set(false);
  }

  private async initMap(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const container = this.mapElementRef?.nativeElement;
    if (!container) return;

    try {
      if (!this.L) {
        const mod: any = await import('leaflet');
        this.L = (mod.default ?? mod) as typeof Leaflet;
      }
      const L = this.L;

      this.destroyMap();

      container.style.direction = 'ltr';
      container.setAttribute('dir', 'ltr');

      const initialLat = this.selectedLat() ?? this.defaultLat;
      const initialLng = this.selectedLng() ?? this.defaultLng;

      this.map = L.map(container, {
        center: [initialLat, initialLng],
        zoom: 14,
        zoomControl: true,
        attributionControl: false,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        tileSize: 256,
        zoomOffset: 0,
      }).addTo(this.map);

      const refreshMap = () => {
        if (this.map) {
          this.map.invalidateSize(true);
        }
      };
      refreshMap();
      setTimeout(refreshMap, 100);
      setTimeout(refreshMap, 300);

      if (this.selectedLat() !== null && this.selectedLng() !== null) {
        this.placeMarker(this.selectedLat()!, this.selectedLng()!);
      } else {
        this.getUserCurrentLocation();
      }

      this.map.on('click', (e: Leaflet.LeafletMouseEvent) => {
        this.placeMarker(e.latlng.lat, e.latlng.lng);
      });
    } catch (err) {
      console.error('Map init error:', err);
    }
  }

  getUserCurrentLocation(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.locationError.set(null);

    if (!('geolocation' in navigator)) {
      this.locationError.set(
        $localize`:@@geolocationUnsupported:Your browser does not support location detection. Please select the location on the map.`,
      );
      return;
    }

    this.isLocating.set(true);
    this.requestPosition(true);
  }

  private requestPosition(highAccuracy: boolean): void {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.isLocating.set(false);
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;

        if (this.map) {
          this.map.flyTo([userLat, userLng], 16, { animate: true });
          this.placeMarker(userLat, userLng);
        }
      },
      (error) => {
        if (error.code === error.TIMEOUT && highAccuracy) {
          this.requestPosition(false);
          return;
        }

        this.isLocating.set(false);
        console.warn('Geolocation Error:', error);
        this.locationError.set(this.getGeolocationErrorMessage(error));
      },
      {
        enableHighAccuracy: highAccuracy,
        timeout: highAccuracy ? 8000 : 15000,
        maximumAge: 60000,
      },
    );
  }

  private getGeolocationErrorMessage(error: GeolocationPositionError): string {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return $localize`:@@geolocationPermissionDenied:Location access is blocked. Allow it from your browser settings, or select the location on the map.`;
      case error.POSITION_UNAVAILABLE:
        return $localize`:@@geolocationUnavailable:Could not determine your location. Make sure location services are on, or select the location on the map.`;
      case error.TIMEOUT:
        return $localize`:@@geolocationTimeout:Detecting your location took too long. Try again, or select the location on the map.`;
      default:
        return $localize`:@@geolocationUnknownError:Could not determine your location. Please select the location on the map.`;
    }
  }

  private parseExistingLocation(): void {
    const value = this.pharmacyForm().get('pharmacyLocation')?.value;
    if (value && typeof value === 'string') {
      const match = value.match(/q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (match && match[1] && match[2]) {
        this.selectedLat.set(parseFloat(match[1]));
        this.selectedLng.set(parseFloat(match[2]));
      }
    }
  }

  private placeMarker(lat: number, lng: number): void {
    if (!this.map || !this.L) return;

    this.locationError.set(null);
    this.selectedLat.set(lat);
    this.selectedLng.set(lng);

    const icon = this.L.divIcon({
      className: 'custom-pin-icon',
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="#C42727"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>`,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
    });

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else {
      this.marker = this.L.marker([lat, lng], { icon }).addTo(this.map);
    }
  }

  confirmLocation(): void {
    const lat = this.selectedLat();
    const lng = this.selectedLng();
    if (lat == null || lng == null) return;

    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    const control = this.pharmacyForm().get('pharmacyLocation');
    if (control) {
      control.setValue(url);
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity();
    }
    this.closeMap();
  }

  private destroyMap(): void {
    if (this.map) {
      this.map.off();
      this.map.remove();
      this.map = null;
    }
    this.marker = null;
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }
}
